import { Panel, InlineWarning, ConfidenceBar, ScoreBadge } from "@/components/common/Primitives";
import { Calculator } from "lucide-react";
import { eur, eurSigned, pct } from "@/lib/format";
import { SCENARIOS, VALUE_ROWS } from "@/components/vehicle/constants";

const BPM_STATUS_CHIP = {
  EXACT: "border-emerald-300 bg-emerald-50 text-emerald-800",
  HOGE_BETROUWBAARHEID: "border-emerald-300 bg-emerald-50 text-emerald-800",
  GESCHAT: "border-blue-300 bg-blue-50 text-blue-800",
};
const BPM_STATUS_CHIP_DEFAULT = "border-amber-300 bg-amber-50 text-amber-800";

export const ScorePanel = ({ head, analysis, onOpenScore }) => (
  <Panel title="Deal Score" testid="score-panel">
    <div className="flex items-start justify-between gap-3 p-3">
      <ScoreBadge score={head.deal_score} advice={head.advice} size="lg" testid="detail-score" />
      <button
        type="button"
        onClick={onOpenScore}
        data-testid="open-score-dialog"
        className="text-[11px] font-semibold text-blue-700 hover:underline"
      >
        Score-opbouw
      </button>
    </div>
    {analysis?.deal_score?.message && (
      <div className="px-3 pb-3">
        <InlineWarning tone="danger">{analysis.deal_score.message}</InlineWarning>
      </div>
    )}
    <div className="grid grid-cols-2 border-t border-slate-100">
      <div className="border-r border-slate-100 px-3 py-2">
        <div className="label-xs">Waarderingsbetrouwbaarheid</div>
        <ConfidenceBar value={head.confidence_pct} label={analysis?.valuation?.confidence_label} />
      </div>
      <div className="px-3 py-2">
        <div className="label-xs">Marktliquiditeit</div>
        <div className="mono mt-0.5 text-[13px] font-semibold text-slate-800">
          {analysis?.liquidity?.label || "—"}
        </div>
        <div className="text-[10px] leading-tight text-slate-500">
          {analysis?.liquidity?.comparable_count ?? 0} NL-advertenties, spreiding{" "}
          {pct(analysis?.liquidity?.price_dispersion_pct, { fallback: "—" })}
        </div>
      </div>
    </div>
  </Panel>
);

export const ValuesPanel = ({ head, scenario, setScenario }) => (
  <Panel title="Drie Nederlandse verkoopwaarden" testid="values-panel">
    <div className="divide-y divide-slate-100">
      {VALUE_ROWS.map(([label, field, key]) => {
        const value = head[field];
        return (
          <button
            key={key}
            type="button"
            onClick={() => setScenario(key)}
            data-testid={`scenario-${key}`}
            className={`flex w-full items-center justify-between px-3 py-2 text-left transition-colors duration-150 ${
              scenario === key ? "bg-blue-50" : "hover:bg-slate-50"
            }`}
          >
            <span className="text-[12.5px] font-medium text-slate-800">{label}</span>
            <span className="mono text-[13px] font-semibold text-slate-900">
              {value === null || value === undefined ? (
                <span className="text-[11px] italic text-red-700">
                  Onvoldoende vergelijkingsauto's
                </span>
              ) : (
                eur(value)
              )}
            </span>
          </button>
        );
      })}
    </div>
    <div className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-500">
      Berekeningen gebruiken standaard de verwachte marktwaarde. Actief scenario:{" "}
      <b>{SCENARIOS.find((s) => s.key === scenario)?.label}</b>.
    </div>
  </Panel>
);

export const BpmPanel = ({ analysis, head, onOpenBpm, onOpenBpmOverride }) => {
  const bpm = analysis?.bpm;
  const unknown = bpm?.bpm_estimate === null || bpm?.bpm_estimate === undefined;
  return (
    <Panel title="BPM" testid="bpm-panel">
      <div className="p-3">
        {analysis?.deal_score?.requires_bpm_validation && (
          <div className="mb-2">
            <InlineWarning tone="warning" testid="preliminary-warning">
              Voorlopige kans — {analysis.deal_score.message || "BPM eerst valideren."} Deze auto wordt
              niet als bevestigde topkans gerangschikt.
            </InlineWarning>
          </div>
        )}
        {unknown ? (
          <InlineWarning tone="danger" testid="bpm-warning">
            {bpm?.message || "BPM onbekend — CO₂ onvoldoende."}
          </InlineWarning>
        ) : (
          <div className="flex items-end justify-between">
            <div>
              <div className="mono text-xl font-semibold text-slate-900" data-testid="detail-bpm">
                {bpm?.has_range ? `${eur(bpm.bpm_low)} – ${eur(bpm.bpm_high)}` : eur(head.bpm)}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                {bpm?.has_range
                  ? `Verwacht ${eur(bpm.bpm_expected)} · gebruikt ${eur(head.bpm)} (conservatief)`
                  : `Bandbreedte ${eur(bpm?.bpm_range_low)} – ${eur(bpm?.bpm_range_high)}`}
              </div>
            </div>
            <span
              className={`chip ${BPM_STATUS_CHIP[bpm?.status] || BPM_STATUS_CHIP_DEFAULT}`}
              data-testid="bpm-status-chip"
            >
              {bpm?.status_label}
              {bpm?.co2_confidence ? ` · CO₂-match ${Math.round(bpm.co2_confidence)}%` : ""}
            </span>
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onOpenBpm}
            data-testid="open-bpm-dialog"
            className="text-[11px] font-semibold text-blue-700 hover:underline"
          >
            BPM berekening bekijken
          </button>
          <button
            type="button"
            onClick={onOpenBpmOverride}
            data-testid="open-bpm-override"
            className="flex items-center gap-1 rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Calculator className="h-3 w-3" /> BPM handmatig aanpassen
          </button>
        </div>
      </div>
    </Panel>
  );
};

export const MaxPurchasePanel = ({ maxPurchase }) => (
  <Panel title="Maximale inkoopprijs" testid="max-purchase-panel">
    {!maxPurchase ? (
      <p className="px-3 py-3 text-[12px] text-slate-500">
        Kan niet worden bepaald zonder betrouwbare Nederlandse waardering.
      </p>
    ) : (
      <div className="divide-y divide-slate-100">
        <div className="flex items-center justify-between px-3 py-1.5">
          <span className="text-[12.5px] text-slate-700">Vraagprijs</span>
          <span className="mono text-[13px]">{eur(maxPurchase.asking_price)}</span>
        </div>
        <div className="flex items-center justify-between bg-slate-50 px-3 py-1.5">
          <span className="text-[12.5px] font-semibold text-slate-800">Doel inkoop</span>
          <span className="mono text-[13px] font-semibold" data-testid="target-purchase-price">
            {eur(maxPurchase.max_purchase_price)}
          </span>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5">
          <span className="text-[12.5px] text-slate-700">Benodigde korting</span>
          <span
            className={`mono text-[13px] font-semibold ${
              (maxPurchase.required_discount ?? 0) > 0 ? "text-amber-700" : "text-emerald-700"
            }`}
          >
            {(maxPurchase.required_discount ?? 0) > 0
              ? eur(maxPurchase.required_discount)
              : "Geen korting nodig"}
          </span>
        </div>
        <div className="px-3 py-1.5 text-[11px] text-slate-500">
          Op basis van vereiste handelsbijdrage {eur(maxPurchase.required_contribution)} en{" "}
          {eur(maxPurchase.other_costs)} overige kosten.
        </div>
      </div>
    )}
  </Panel>
);

export const ScenariosPanel = ({ analysis, scenario }) => (
  <Panel title="Winstscenario's" testid="scenarios-panel">
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Scenario</th>
            <th>Verkoopwaarde (incl. BTW)</th>
            <th>Netto NL</th>
            <th>Bijdrage</th>
            <th>ROI</th>
            <th>Break-even</th>
            <th>Max inkoop</th>
          </tr>
        </thead>
        <tbody>
          {SCENARIOS.map((s) => {
            const sc = analysis?.scenarios?.[s.key] || {};
            return (
              <tr
                key={s.key}
                className={scenario === s.key ? "bg-blue-50" : ""}
                data-testid={`scenario-row-${s.key}`}
              >
                <td>
                  <div className="text-[12.5px] font-semibold text-slate-800">{s.label}</div>
                  <div className="text-[10px] text-slate-500">{s.note}</div>
                </td>
                <td className="num">{eur(sc.sale_value)}</td>
                <td className="num text-slate-600">{eur(sc.net_sale_value, { fallback: "—" })}</td>
                <td
                  className={`num font-semibold ${
                    (sc.contribution ?? 0) > 0 ? "text-emerald-700" : "text-red-700"
                  }`}
                >
                  {eurSigned(sc.contribution, { fallback: "—" })}
                </td>
                <td className="num">{pct(sc.roi_pct, { fallback: "—" })}</td>
                <td className="num">{eur(sc.break_even_price)}</td>
                <td className="num">{eur(sc.max_purchase_price?.max_purchase_price)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Panel>
);
